import { useCallback } from 'react';
import { useToast } from '../../../redux/actions/UI';
import { InvalidDataError } from '../api';
import { validateAllStages } from '../utils/stageValidation';

/**
 * Shared hook for campaign editor logic (CreateCampaign and EditCampaign)
 * Extracts common validation and navigation logic
 *
 * @param {Object} options
 * @param {Function} options.saveNow - Function to save campaign
 * @param {Function} options.hasFailed - Function to check if autosave has failed
 * @param {Object} options.currentCampaignData - Current campaign data
 * @param {Function} options.onBeforeStepChangeCustom - Optional custom logic to run before step change (e.g., campaign creation)
 * @param {Function} options.onAfterValidation - Optional callback after validation passes (e.g., save if dirty)
 * @returns {Object} { handleBeforeStepChange }
 */
export const useCampaignEditorLogic = ({
  saveNow,
  hasFailed,
  currentCampaignData,
  onBeforeStepChangeCustom,
  onAfterValidation,
}) => {
  const { errorToast } = useToast();

  // Save before step transitions - shared validation logic
  const handleBeforeStepChange = useCallback(
    async (newStep, _params, editorData) => {
      // Run custom logic first if provided
      if (onBeforeStepChangeCustom) {
        const customResult = await onBeforeStepChangeCustom(
          newStep,
          _params,
          editorData
        );
        // If custom logic returns false, skip default validation
        if (customResult === false) {
          return;
        }
      }

      // Try to save before navigation if autosave has failed
      // Saving is allowed without validation campaign completeness
      if (hasFailed()) {
        try {
          await saveNow();
        } catch (error) {
          // Log error and prevent navigation - saving must succeed before navigation
          console.error('Failed to save before navigation:', error);
          throw error;
        }
      }

      // Prevent navigation to audience or review screens if stages are invalid
      // Navigation validation is enforced, but saving is not blocked
      if (newStep === 'audience/editor' || newStep === 'review-campaign') {
        const stages = editorData?.stages || currentCampaignData?.stages || [];
        const areAllStagesValid = validateAllStages(stages);

        if (!areAllStagesValid) {
          errorToast(
            'Some stages are invalid. Please correct them and try again.',
            5
          );
          throw new InvalidDataError([
            'Some stages are invalid. Please correct them and try again.',
          ]);
        }
      }

      // When navigating to review-campaign, also validate audience
      // This ensures both stages AND audience are valid before allowing review
      if (newStep === 'review-campaign') {
        // Use editorData as single source of truth (synced from Redux when user edits audience)
        // Fallback to currentCampaignData only if editorData is missing (defensive)
        const audience = editorData?.audience || currentCampaignData?.audience;
        const hasTopics =
          audience?.topicIds &&
          Array.isArray(audience.topicIds) &&
          audience.topicIds.length > 0;

        // Require at least one topic to be selected
        if (!hasTopics) {
          // Show error toast - this must be called before throwing the error
          errorToast(
            'Cannot navigate to review: Please select at least one topic for your audience.',
            5
          );
          // Throw error to prevent navigation
          throw new InvalidDataError([
            'Cannot navigate to review: Please select at least one topic for your audience.',
          ]);
        }
      }

      // Run custom after-validation logic if provided (e.g., save if dirty)
      if (onAfterValidation) {
        await onAfterValidation();
      }
    },
    [
      saveNow,
      hasFailed,
      currentCampaignData,
      onBeforeStepChangeCustom,
      onAfterValidation,
    ]
  );

  return {
    handleBeforeStepChange,
  };
};
